// Pure logic for the Vault tab's "Storage protection" card: when it shows,
// what its status pill says, and how "Open storage console" gets the brain's
// /atrest page open in a new tab, signed in. No React and no api client at
// runtime, so `node --test` covers it.

import type { AtrestState, OperatorCertificate } from "./api.ts";

export type StorageProtectionTone = "green" | "amber" | "blue";

export interface StorageProtectionView {
  label: string;
  tone: StorageProtectionTone;
  // One line under the title; the sealed line says where unlocking happens.
  note: string;
}

// Only a password-protected brain needs a person: the console is where they
// set the password, seal, and unlock. Brains keyed by a file on their disk or
// by a vault, brains without encryption at rest, and brains the backend can't
// read show no card.
const VIEWS: Partial<Record<AtrestState["status"], StorageProtectionView>> = {
  sealed: {
    label: "Locked",
    tone: "amber",
    note: "To unlock it, enter the password in the storage console. The dashboard can't unlock it for you.",
  },
  active: {
    label: "Unlocked",
    tone: "green",
    note: "This Boo's stored data is protected by a password. Lock it again or check its state in the storage console.",
  },
  uninitialized: {
    label: "Needs a password",
    tone: "blue",
    note: "This Boo waits for a password before it can store data. Set one in the storage console.",
  },
};

export function storageProtectionView(state: AtrestState | null | undefined): StorageProtectionView | null {
  if (!state || state.primaryType !== "password") return null;
  return VIEWS[state.status] ?? null;
}

// The tab the console opens in. It is opened blank inside the click so the
// browser counts it as the user's, then pointed at the console once the
// certificate is back.
export interface ConsoleTab {
  opener: unknown;
  location: { href: string };
  close(): void;
}

export class ConsoleOpenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConsoleOpenError";
  }
}

export const POPUP_BLOCKED_MESSAGE =
  "Your browser blocked the new tab. Allow pop-ups for this site and try again.";
const BAD_URL_MESSAGE = "The storage console link came back malformed. Try again.";

// The backend builds the URL (its host must match the certificate's
// audience); refuse anything that isn't an https /atrest page carrying the
// certificate in the fragment. Plain http is accepted only for a local brain.
export function consoleUrl(answer: OperatorCertificate | null | undefined): string | null {
  if (!answer || typeof answer.url !== "string") return null;
  let url: URL;
  try {
    url = new URL(answer.url);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && !(url.protocol === "http:" && isLocalHost(url.hostname))) return null;
  if (url.pathname !== "/atrest") return null;
  if (!/^#oc=[^&]+/.test(url.hash)) return null;
  return url.toString();
}

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost");
}

export async function openStorageConsole(
  tab: ConsoleTab | null,
  mint: () => Promise<OperatorCertificate>,
): Promise<void> {
  if (!tab) throw new ConsoleOpenError(POPUP_BLOCKED_MESSAGE);
  try {
    const target = consoleUrl(await mint());
    if (!target) throw new ConsoleOpenError(BAD_URL_MESSAGE);
    // The console is another site: give it no handle back to the dashboard.
    tab.opener = null;
    tab.location.href = target;
  } catch (error) {
    tab.close();
    throw error;
  }
}
